import { useEffect, useRef, useState } from 'react'
import {
  SFU_TAKEN_OVER_ERROR,
  createReconnectBannerTimer,
  reconnectReason,
  type ReconnectBannerTimer,
  type ReconnectReason,
} from '../services/reconnect-banner'

/** The browser's own idea of whether it has a network; anything but an explicit false is online. */
function browserOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false
}

/**
 * What the in-meeting "Reconnecting…" banner should say right now, or null.
 *
 * Armed only once this room has been fully connected — the channel joined AND
 * the media session up — so the seconds of an ordinary join never show it,
 * and re-armed per room: a breakout move rebuilds both connections from
 * scratch, which is a join, not a reconnect. `roomId` is null once the
 * meeting is being left (the credentials are cleared first), which disarms it
 * before the teardown that follows could show it.
 */
export function useReconnectBanner(s: {
  roomId: string | null
  channelJoined: boolean
  sfuReady: boolean
  error: string | null
}): ReconnectReason | null {
  const [online, setOnline] = useState(browserOnline)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const sync = () => setOnline(browserOnline())
    window.addEventListener('online', sync)
    window.addEventListener('offline', sync)
    // A page restored from the back/forward cache missed every event fired
    // while it was frozen.
    window.addEventListener('pageshow', sync)
    // And one that fired between the first render's read and this effect.
    sync()
    return () => {
      window.removeEventListener('online', sync)
      window.removeEventListener('offline', sync)
      window.removeEventListener('pageshow', sync)
    }
  }, [])

  // Armed per VISIT to a room, not per room id. The render that switches rooms
  // — a breakout move, or the return — still carries the old room's flags,
  // because both teardowns are effect cleanups that run after it; arming on it
  // armed the new room before it had connected, and an ordinary move showed
  // "Reconnecting…". And the latch was never cleared, so returning to a room
  // armed it at once. So a change of room clears it, and that render cannot
  // arm: only a later one that sees the new room connected can.
  const armedForRef = useRef<string | null>(null)
  const lastRoomRef = useRef<string | null>(s.roomId)
  if (lastRoomRef.current !== s.roomId) {
    lastRoomRef.current = s.roomId
    armedForRef.current = null
  } else if (s.roomId && s.channelJoined && s.sfuReady) {
    armedForRef.current = s.roomId
  }
  const armed = s.roomId !== null && armedForRef.current === s.roomId

  const reason = armed
    ? reconnectReason({
        online,
        channelJoined: s.channelJoined,
        sfuReady: s.sfuReady,
        sfuError: !!s.error,
        sfuTakenOver: s.error === SFU_TAKEN_OVER_ERROR,
      })
    : null

  const [visible, setVisible] = useState<ReconnectReason | null>(null)
  const timerRef = useRef<ReconnectBannerTimer | null>(null)
  useEffect(() => {
    const timer = createReconnectBannerTimer({ onChange: setVisible })
    timerRef.current = timer
    return () => {
      timer.dispose()
      timerRef.current = null
    }
  }, [])

  useEffect(() => {
    timerRef.current?.update(reason)
  }, [reason])

  // Disarmed (leaving, or a different room) is not a recovery to wait out:
  // nothing should be on screen at all.
  return armed ? visible : null
}
